import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { budgetPeriodFromQuery, budgetPeriodToQuery, defaultBudgetPeriod, normalizeBudgetPeriod, shiftPeriod, type BudgetPeriodRange } from "../lib/budgetPeriod";
import { BUDGET_LINES_NEW_PARAM } from "../lib/budgetRoutes";
import { currentMonth } from "../lib/format";
import { applyMonthRange } from "../lib/monthRange";

// Periodo de Presupuestos (#1332): un solo «desde – hasta» de meses completos
// que gobierna toda la página (Dashboard y Desglose). Vive en la URL
// (`?desde=&hasta=`), así que un enlace abre el mismo periodo, y en la sesión del
// navegador, para que no se pierda al pasar de una página a otra por el menú.

export type BudgetPeriodPreset = "month" | "quarter" | "year" | "next3" | "last3";

export const BUDGET_PERIOD_PRESETS: BudgetPeriodPreset[] = ["month", "quarter", "year", "next3", "last3"];

/** Rango de cada atajo, relativo al mes en curso. */
export function presetRange(preset: BudgetPeriodPreset, month: string): BudgetPeriodRange {
  if (preset === "month") return { from: month, to: month };
  if (preset === "next3") return { from: month, to: shiftPeriod(month, 2) };
  if (preset === "last3") return { from: shiftPeriod(month, -3), to: shiftPeriod(month, -1) };
  if (preset === "quarter") {
    const start = shiftPeriod(month, -((Number(month.slice(5, 7)) - 1) % 3));
    return { from: start, to: shiftPeriod(start, 2) };
  }
  return defaultBudgetPeriod(month);
}

const STORAGE_KEY = "expenses.budgetPeriod";

function remembered(): Partial<BudgetPeriodRange> | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<BudgetPeriodRange>) : null;
  } catch {
    return null;
  }
}

function remember(range: BudgetPeriodRange, isDefault: boolean) {
  try {
    if (isDefault) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(range));
  } catch {
    // Sin almacenamiento (modo privado estricto): el periodo sigue en la URL.
  }
}

/** El periodo con que abre la página: el de la URL; si no trae, el último elegido en esta sesión. */
function initialPeriod(): BudgetPeriodRange {
  const month = currentMonth();
  const q = new URLSearchParams(window.location.search);
  if (q.has("desde") || q.has("hasta")) return budgetPeriodFromQuery(window.location.search, month);
  const saved = remembered();
  return saved ? normalizeBudgetPeriod(saved, month) : defaultBudgetPeriod(month);
}

export interface BudgetPeriodState {
  range: BudgetPeriodRange;
  /** Lo tecleado a medias donde <input type="month"> se ve como texto. */
  draft: BudgetPeriodRange;
  isDefault: boolean;
  /** Atajo que coincide con el rango actual, si alguno. */
  preset: BudgetPeriodPreset | null;
  change: (change: { from?: string; to?: string }) => void;
  apply: (next: BudgetPeriodRange) => void;
  usePreset: (preset: BudgetPeriodPreset) => void;
  reset: () => void;
  resetDraft: () => void;
}

export function useBudgetPeriod(): BudgetPeriodState {
  const [range, setRange] = useState<BudgetPeriodRange>(initialPeriod);
  const [draft, setDraft] = useState(range);
  useEffect(() => setDraft(range), [range]);
  const location = useLocation();

  // La URL y la sesión siguen al periodo. También tras una navegación del menú
  // a esta misma página, que deja la dirección sin `?desde=&hasta=`.
  useEffect(() => {
    const month = currentMonth();
    const def = defaultBudgetPeriod(month);
    remember(range, range.from === def.from && range.to === def.to);
    try {
      const { pathname, search, hash } = window.location;
      // `?new=1` ya hizo su trabajo al llegar: no se arrastra con el periodo.
      const rest = new URLSearchParams(search);
      rest.delete(BUDGET_LINES_NEW_PARAM);
      const next = budgetPeriodToQuery(range, month, rest.toString());
      if (next !== search) window.history.replaceState(window.history.state, "", `${pathname}${next}${hash}`);
    } catch {
      // Sin historial (vista previa, iframe): el rango sigue funcionando sin URL.
    }
  }, [range, location.key]);

  const apply = useCallback((next: BudgetPeriodRange) => {
    const clean = normalizeBudgetPeriod(next, currentMonth());
    setRange((r) => (r.from === clean.from && r.to === clean.to ? r : clean));
  }, []);

  const change = useCallback(
    (delta: { from?: string; to?: string }) => {
      setDraft((d) => ({ ...d, ...delta }));
      const next = applyMonthRange(range, delta);
      if (next.from !== range.from || next.to !== range.to) apply(next);
    },
    [range, apply],
  );

  const month = currentMonth();
  const def = defaultBudgetPeriod(month);
  const preset = BUDGET_PERIOD_PRESETS.find((p) => {
    const r = presetRange(p, month);
    return r.from === range.from && r.to === range.to;
  });

  return {
    range,
    draft,
    isDefault: range.from === def.from && range.to === def.to,
    preset: preset ?? null,
    change,
    apply,
    usePreset: (p) => apply(presetRange(p, currentMonth())),
    reset: () => apply(defaultBudgetPeriod(currentMonth())),
    resetDraft: () => setDraft(range),
  };
}
